/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import {catchError, finalize, forkJoin, Observable, of} from 'rxjs';
import template from './site-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {SignalAdminResourcePage} from '../common/controller/signal-admin-resource-api';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {normalizeSiteIabCategories} from './site-commons';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SiteApiService, SiteLookupModel, SiteModel} from './site-api.service';
import {SiteCreativeSummaryState} from './site-creative-summary-state';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {SiteCreativeSummaryComponent} from './site-creative-summary.component';
@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED, SiteCreativeSummaryComponent],
  selector: 'adn-site-edit-page',
  template
})
export class SiteEditPageComponent extends SignalAdminResourceEditPageBase<SiteModel> {
  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly publisherItemLink = createObjectTypeItemLink('EarningsAccount');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');

  readonly scanCreativesPolicyOptions: Array<{ value: string | null; label: string; hint: string }> = [
    {value: null, label: 'No scanning', hint: 'No scanning is required for site'},
    {value: 'SAFE_DOMAIN_COOKIE_500', label: 'Schibsted Norway Policy', hint: 'Scan creatives and apply Schibsted Norway policy'},
    {value: 'SCHIBSTED_SWEDEN', label: 'Schibsted Sweden Policy', hint: 'Scan creatives and apply Schibsted Sweden policy'},
    {value: 'TV2_NO_COOKIE_500', label: 'TV2.no Policy', hint: 'Scan creatives and apply TV2.no policy'}
  ];

  showBlockLists = 'ACTIVE';
  showScannerBlockedCreatives = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showSiteGroupsEditable = true;
  isAxPublisher = false;
  showMerchantSelection = false;
  showPaywalled = false;
  showScannerToggle = false;
  adminUserName = '';
  showOwnership = false;
  canUpdateSite = false;
  canEditAdvertisingAccess = false;
  canEditSiteTeams = false;
  canEditMarketplaceOwner = false;
  teamsWarning = false;
  politicalAdvertising: 'BLOCKED' | 'UNBLOCKED' = 'UNBLOCKED';

  availableMerchants: any[] = [];
  earningsAccounts: SiteLookupModel[] = [];
  siteGroups: SiteLookupModel[] = [];
  blocklists: SiteLookupModel[] = [];
  rulesets: SiteLookupModel[] = [];
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];
  allTeams: SiteLookupModel[] = [];
  allMarketplacePublisherTeams: any[] = [];
  allNotAxPublisherTeams: any[] = [];

  readonly lookupsLoading = signal(true);
  readonly lookupLoadFailed = signal(false);

  showAllowedTech = false;
  blockedCreativeSummary: SiteCreativeSummaryState | null = null;
  initiallyBlockedCreativeSummary: SiteCreativeSummaryState | null = null;
  allowedCreativeSummary: SiteCreativeSummaryState | null = null;

  private readonly siteApi = inject(SiteApiService);
  private readonly siteDestroyRef = inject(DestroyRef);
  private creativeSummarySiteId: string | null = null;

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(SiteApiService), 'site');

    this.showScannerBlockedCreatives = LocalNetworkProfile.isExperimental();
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
    this.showSiteGroupsEditable = !this.pureMarketplacePlatform || LocalUserPermissions.hasAny('MANAGE_PUBLISHING');
    this.isAxPublisher = LocalUserPermissions.isAnyAxPublisher();

    const username = localUserProfile.get('username');
    if (_.includes(['adnuntius@adnuntius.com', 'broker1@adnuntius.com'], username)) {
      this.adminUserName = username;
      this.showPaywalled = true;
      if (this.showScannerBlockedCreatives) {
        this.showScannerToggle = true;
      }
    }

    const networkInfo = this.localNetworkInfo;
    this.availableMerchants = _.filter(networkInfo.getInfo('productFeedProductCounts') || [], (feed: any) => {
      return feed.productCount > 0;
    });
    this.showMerchantSelection = this.availableMerchants.length > 0;

    this.canUpdateSite = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalUserPermissions.hasAny('MANAGE_PUBLISHING')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN')
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.canEditAdvertisingAccess = LocalUserPermissions.hasNetwork('MANAGE_BUSINESS')
      || LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.canEditMarketplaceOwner = this.canEditAdvertisingAccess;
    this.canEditSiteTeams = this.canEditAdvertisingAccess
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || LocalUserPermissions.hasAny('AX_PUBLISHER_TEAM_ADMIN');

    this.loadLookups();
  }

  get politicalAdvertisingBlocked(): boolean {
    return this.politicalAdvertising === 'BLOCKED';
  }

  set politicalAdvertisingBlocked(blocked: boolean) {
    this.politicalAdvertising = blocked ? 'BLOCKED' : 'UNBLOCKED';
    this.markExternalFormDirty();
  }

  get scanCreativesPolicyHint(): string {
    const policy = this.editingModel?.scanCreativesPolicy ?? null;
    const option = _.find(this.scanCreativesPolicyOptions, {value: policy});
    return option ? option.hint : this.scanCreativesPolicyOptions[0].hint;
  }

  compareScanCreativesPolicy(left: string | null | undefined, right: string | null | undefined): boolean {
    return (left ?? null) === (right ?? null);
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    this.editingModel.filterTeamsType = this.editingModel.filterTeamsType || 'NONE';
    if (this.isNew || this.editingModel.scanCreativesPolicy === undefined) {
      this.editingModel.scanCreativesPolicy = null;
    }
    this.normalizeMerchantIds();
    normalizeSiteIabCategories(this.editingModel);
    this.runAfterInitAfterSave();
    if (!this.isNew) {
      this.initCreativeSummaryIfNeeded();
    }
  }

  protected override afterSave(model: SiteModel): void {
    this.teamsWarning = (this.editingModel.teams || []).length !== (model.teams || []).length;
  }

  protected override prepareForSubmit(model: SiteModel): void {
    if (model.politicalAdvertising !== this.politicalAdvertising) {
      if (this.politicalAdvertising === 'BLOCKED') {
        model.politicalAdvertising = 'BLOCKED';
      } else {
        model.politicalAdvertising = 'ALLOWED';
      }
    }
    if (!model.country) {
      model.country = null;
    }
    const submitMids = _.map(model.retailMerchantIds || [], (mid: any) => {
      return _.isString(mid) ? parseInt(mid, 10) : mid;
    });
    model.retailMerchantIds = _.uniq(_.filter(submitMids, _.isFinite));
    normalizeSiteIabCategories(model);
    model.siteGroups = _.map(model.siteGroups || [], (sg: any) => {
      return {id: sg.id, name: sg.name};
    });
    if (model.ownerTeam) {
      model.teams = _.filter(model.teams || [], (teamId: string) => {
        return !!_.find(this.allNotAxPublisherTeams, (team: any) => team.id === teamId);
      });
    }
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  iabCategoriesChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  selectTeams(): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    this.editingModel.teams = _.map(this.allNotAxPublisherTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    if (!this.canEditSiteTeams) {
      return;
    }
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);
    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }
    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  isMerchantSelected(merchantId: number): boolean {
    return (this.editingModel.retailMerchantIds || []).indexOf(merchantId) > -1;
  }

  toggleMerchant(merchantId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const mids = [...(this.editingModel.retailMerchantIds || [])];
    const index = mids.indexOf(merchantId);
    if (checked && index < 0) {
      mids.push(merchantId);
    } else if (!checked && index > -1) {
      mids.splice(index, 1);
    }
    this.editingModel.retailMerchantIds = mids;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  trackByMerchantId(_index: number, merchant: any): number {
    return merchant.id;
  }

  private runAfterInitAfterSave(): void {
    const data = this.editingModel;
    this.editingModel.renderOption = this.editingModel.renderOption || null;
    this.editingModel.teams = _.map(_.get(data, 'teams') || this.editingModel.teams || [], (team: any) => {
      return _.isString(team) ? team : team.id;
    });
    this.normalizeMerchantIds(data);
    this.editingModel.scanCreativesPolicy = data.scanCreativesPolicy ? data.scanCreativesPolicy : null;

    this.politicalAdvertising = 'BLOCKED';
    if (!this.editingModel.politicalAdvertising || this.editingModel.politicalAdvertising !== 'BLOCKED') {
      this.politicalAdvertising = 'UNBLOCKED';
    }

    this.resolveEarningsAccountReference();
    this.resolveSiteGroupReferences();
    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
    this.resolveOwnerTeamReference();
    if (!this.isNew) {
      this.initCreativeSummaryIfNeeded();
    }
  }

  private normalizeMerchantIds(data?: any): void {
    const source = data || this.editingModel;
    const savedMerchantIds = _.get(source, 'retailMerchantIds') || this.editingModel.retailMerchantIds || [];
    const coercedMids = _.map(savedMerchantIds, (mid: any) => {
      return _.isString(mid) ? parseInt(mid, 10) : mid;
    });
    this.editingModel.retailMerchantIds = _.filter(coercedMids, _.isFinite);
  }

  private initCreativeSummaryIfNeeded(): void {
    if (!this.showScannerBlockedCreatives || !this.editingModel?.scanCreativesPolicy || !this.editingModel?.id) {
      this.creativeSummarySiteId = null;
      this.blockedCreativeSummary = null;
      this.initiallyBlockedCreativeSummary = null;
      this.allowedCreativeSummary = null;
      return;
    }
    if (this.creativeSummarySiteId === this.editingModel.id) {
      return;
    }
    this.creativeSummarySiteId = this.editingModel.id;
    const profile = this.localUserProfile.getProfile() || {};
    const pageSize = Math.max(1, Math.min(100, profile.adnPrefs?.defaultPageSize || 25));
    const locale = this.localUserProfile.get('locale');
    this.blockedCreativeSummary = new SiteCreativeSummaryState(
      this.siteApi, this.editingModel.id, 'BLOCKED', pageSize, locale, this.siteDestroyRef
    );
    this.blockedCreativeSummary.load();
    if (this.showScannerToggle) {
      this.initiallyBlockedCreativeSummary = new SiteCreativeSummaryState(
        this.siteApi, this.editingModel.id, 'INITIALLY_BLOCKED', pageSize, locale, this.siteDestroyRef
      );
      this.allowedCreativeSummary = new SiteCreativeSummaryState(
        this.siteApi, this.editingModel.id, 'ALLOWED', pageSize, locale, this.siteDestroyRef
      );
      this.initiallyBlockedCreativeSummary.load();
      this.allowedCreativeSummary.load();
    }
  }

  private loadLookups(): void {
    this.lookupsLoading.set(true);
    this.lookupLoadFailed.set(false);
    forkJoin({
      earningsAccounts: this.lookupOrEmpty(this.siteApi.listEarningsAccounts()),
      siteGroups: this.lookupOrEmpty(this.siteApi.listSiteGroups()),
      blocklists: this.lookupOrEmpty(this.siteApi.listBlocklists()),
      rulesets: this.pureMarketplacePlatform
        ? this.lookupOrEmpty(this.siteApi.listRulesets())
        : of({results: [], totalCount: 0} as SignalAdminResourcePage<SiteLookupModel>),
      teams: this.lookupOrEmpty(this.siteApi.listTeams())
    }).pipe(
      finalize(() => this.lookupsLoading.set(false)),
      takeUntilDestroyed(this.siteDestroyRef)
    ).subscribe((lookups) => {
      this.earningsAccounts = lookups.earningsAccounts.results || [];
      this.siteGroups = lookups.siteGroups.results || [];
      this.blocklists = lookups.blocklists.results || [];
      this.rulesets = lookups.rulesets.results || [];
      this.siteRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'INVENTORY');
      this.targetingRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'TARGETING');
      this.sizeRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'SIZE');
      this.layoutRulesets = _.filter(this.rulesets, (rs: any) => rs.type === 'LAYOUT');
      this.allTeams = lookups.teams.results || [];
      this.allMarketplacePublisherTeams = _.filter(this.allTeams, (team: any) => team.type === 'AX_PUBLISHER');
      this.allNotAxPublisherTeams = _.filter(this.allTeams, (team: any) => {
        return team.type !== 'AX_PUBLISHER' && team.type !== 'CREATIVE_AGENCY';
      });
      this.showOwnership = this.allMarketplacePublisherTeams.length > 0;
      this.resolveEarningsAccountReference();
      this.resolveSiteGroupReferences();
      this.resolveBlocklistReference();
      this.resolveRulesetReferences();
      this.resolveOwnerTeamReference();
    });
  }

  private lookupOrEmpty(
    source: Observable<SignalAdminResourcePage<SiteLookupModel>>
  ): Observable<SignalAdminResourcePage<SiteLookupModel>> {
    return source.pipe(catchError(() => {
      this.lookupLoadFailed.set(true);
      return of({results: [], totalCount: 0});
    }));
  }

  private resolveEarningsAccountReference(): void {
    if (!this.editingModel?.earningsAccount?.id) {
      return;
    }
    const match = _.find(this.earningsAccounts, ['id', this.editingModel.earningsAccount.id]);
    if (match) {
      this.editingModel.earningsAccount = match;
    }
  }

  private resolveSiteGroupReferences(): void {
    if (!this.editingModel?.siteGroups?.length) {
      return;
    }
    this.editingModel.siteGroups = _.map(this.editingModel.siteGroups, (sg: any) => {
      const match = _.find(this.siteGroups, ['id', sg.id]);
      return match || sg;
    });
  }

  private resolveBlocklistReference(): void {
    if (!this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists, ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (this.editingModel.siteRuleset?.id) {
      const siteRuleset = _.find(this.rulesets, ['id', this.editingModel.siteRuleset.id]);
      if (siteRuleset) {
        this.editingModel.siteRuleset = siteRuleset;
      }
    }
  }

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam) {
      return;
    }
    const ownerTeamId = _.isString(this.editingModel.ownerTeam)
      ? this.editingModel.ownerTeam
      : this.editingModel.ownerTeam.id;
    const match = _.find(this.allMarketplacePublisherTeams, ['id', ownerTeamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    } else {
      const currentOwnerTeam = _.isString(this.editingModel.ownerTeam)
        ? {id: this.editingModel.ownerTeam}
        : this.editingModel.ownerTeam;
      this.editingModel.ownerTeam = currentOwnerTeam;
      this.allMarketplacePublisherTeams = [currentOwnerTeam, ...this.allMarketplacePublisherTeams];
    }
  }
}
